import React, { ChangeEvent } from "react";
import { ButtonRoot } from "./SecondaryButton";
import { sanitizeInput, sanitizeValue } from "../Input/IncrementNumberInput";

interface StepperProps extends React.HTMLAttributes<HTMLDivElement> {
  min?: number;
  max?: number;
  value: number;
  allowDecimal?: boolean;
  disabled?: boolean;
  disableSanitize?: boolean;
  onValueChange: (value: number) => void;
  width?: "full";
}

export const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      min = 0,
      max = Number.MAX_VALUE,
      value = 0,
      onValueChange,
      className,
      disableSanitize = false,
      disabled = false,
      allowDecimal = false,
      width,
    },
    ref
  ) => {
    const sanitizedValue = value != null ? sanitizeValue(value) : "";

    function handleInputChange(newValue: number) {
      const paddedNewValues = sanitizeValue(newValue, min, max);
      onValueChange(paddedNewValues);
    }

    function adjustInput(e: ChangeEvent<HTMLInputElement>) {
      e.stopPropagation();
      const number = e.target.valueAsNumber;
      const value = allowDecimal ? number : Math.round(number);
      handleInputChange(value);
    }

    // When the user leaves an empty field, commit a real number so the cart
    // doesn't carry NaN forever. While the field is focused we deliberately
    // let NaN ride so the input can render empty and the user can re-type.
    function handleBlur() {
      if (!Number.isFinite(value)) {
        onValueChange(min);
      }
    }

    // +/- buttons treat a NaN/empty current value as "start from min" so
    // clicking + on an empty field jumps to min+1 instead of staying empty.
    const safeValue = Number.isFinite(value) ? value : min;

    const widthClass = width === "full" ? "w-full" : "w-32";

    return (
      <div
        className={`bg-gray-100 rounded-md border flex align-middle ${widthClass} h-7 ${className}`}
      >
        <ButtonRoot
          onClick={(e) => {
            e.stopPropagation();
            handleInputChange(safeValue - 1);
          }}
          className="disabled:bg-gray-200 content-center w-[40px] text-center text-xs font-semibold h-full"
          disabled={disabled || (Number.isFinite(value) && value === min)}
        >
          -
        </ButtonRoot>
        <div className="w-[1px] bg-gray-200" />
        <label
          onClick={(e) => e.stopPropagation()}
          className="flex-1 text-xs font-semibold min-w-8 w-8 cursor-text"
        >
          <input
            onKeyDown={disableSanitize ? undefined : sanitizeInput}
            key="stepper input"
            className="bg-transparent outline-none w-full h-full text-center px-2 cursor-text"
            type="number"
            onChange={adjustInput}
            onBlur={handleBlur}
            value={
              typeof sanitizedValue === "number" && Number.isFinite(sanitizedValue)
                ? sanitizedValue
                : ""
            }
          />
        </label>
        <div className="w-[1px] bg-gray-200" />
        <ButtonRoot
          onClick={(e) => {
            e.stopPropagation();
            handleInputChange(safeValue + 1);
          }}
          className="content-center w-[40px] text-center text-xs font-semibold h-full"
          disabled={disabled || (Number.isFinite(value) && value === max)}
        >
          +
        </ButtonRoot>
      </div>
    );
  }
);
